import {
  Box,
  Divider,
  Flex,
  CloseButton,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useBreakpointValue
} from '@chakra-ui/react';
import {
  Accordion,
  Badge,
  Button,
  ButtonText,
  ButtonIcon,
  Checkbox,
  Input,
  Modal,
  Radio,
  Select,
  useToast
} from '@morressier/alembic';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faInfoCircle,
  faPlus,
  faTrash,
  faTriangleExclamation
} from '@fortawesome/pro-regular-svg-icons';
import {
  fetchOrganization,
  fetchWorkspaceMergeAccountSearch,
  fetchWorkspaceMergeAccountSearchFields,
  IMergeAccountSearchField,
  IMergeAccountSearchFilter
} from 'src/api/queries';
import { useWorkspaceMergeAccounts } from 'src/api/mutations';
import {
  fetchRinggoldOrganization,
  IOrganizations
} from 'src/api/signUpMutation';
import SubmissionLoader from '../../Submissions/SubmissionLoader';
import UserItem from '../../SubmissionContexts/Users/UserItem';
import {
  IAffiliationDetails,
  IJournalWiseUserRoleList,
  IUsersListItemResponse
} from '@publishing-engine/domain/models';
import { useParams } from 'react-router-dom';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import { formatDate } from 'src/utils/date';
import { Overlay } from 'src/components/Overlay';
import SubmittedAuthorDetailsModal from 'src/components/Submission/AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';
import NavbarDashboard from 'src/components/NavbarDashboard';
import ConfirmMergeModal from './ConfirmMergeModal';
import FetchDuplicateAccountsModal, {
  DuplicateAccountField
} from './FetchDuplicateAccountsModal';
import MergeAccountsSuccessState from './MergeAccountsSuccessState';
import UserTagBadgeList, { MergeUserTagSummary } from './UserTagBadgeList';

const LIMIT = 10;
const DUPLICATE_ACCOUNT_FETCH_LIMIT = 10;
const MAX_SEARCH_ROWS = 10;
const MIN_MERGE_ACCOUNTS = 2;
const MAX_MERGE_ACCOUNTS = 5;
const MERGE_ACCOUNTS_TOOLTIP =
  'Combine duplicate accounts into one. Your data, settings, and activity will be carried over, and you will have a chance to review everything before any changes are made.';
const RESOLVE_CONFLICTS_TOOLTIP =
  'Review the differences between these accounts below. For conflicting information, select the value you want to keep in your primary profile. Fields with matching data have been merged automatically.';
const MERGE_ACCOUNT_HELP_TITLE =
  'Account Merge - What Happens When You Merge Accounts?';
const MERGE_ACCOUNT_HELP_SECTIONS = [
  {
    title: 'Primary Account',
    lines: [
      'The selected Primary Account remains active after the merge.',
      'The Secondary Account is deactivated and can no longer be used to sign in.',
      'All eligible information from the Secondary Account is transferred to the Primary Account.'
    ]
  },
  {
    title: 'Author Submissions',
    items: [
      {
        question: 'What happens if the Primary Account has a live submission?',
        answers: [
          'The merge is allowed.',
          'The Primary Account remains the owner of the live submission.',
          'Completed submissions from the Secondary Account are transferred to the Primary Account.'
        ]
      },
      {
        question:
          'What happens if the Secondary Account has a live submission?',
        answers: [
          'The Secondary Account must be selected as the Primary Account before merging.',
          'The merge cannot proceed if the live submission account is chosen as the Secondary Account.'
        ]
      },
      {
        question: 'What happens if both accounts have live submissions?',
        answers: [
          'The merge is blocked.',
          'Neither account can be merged into the other while both have active submissions.'
        ]
      },
      {
        question: 'What happens if neither account has a live submission?',
        answers: ['Either account can be selected as the Primary Account.']
      },
      {
        question: 'What happens to completed submissions?',
        answers: [
          'All published and completed submissions remain intact.',
          'Ownership is transferred to the Primary Account.',
          'Submission files and publication records are unchanged.'
        ]
      },
      {
        question: 'What happens to draft submissions?',
        answers: [
          'Drafts are transferred to the Primary Account.',
          'Drafts do not prevent an account merge.'
        ]
      }
    ]
  },
  {
    title: 'Reviewer Activity',
    items: [
      {
        question: 'What happens to pending review invitations?',
        answers: [
          'Pending invitations are transferred to the Primary Account.',
          'Future reviewer emails are sent to the Primary Account.'
        ]
      },
      {
        question: 'What happens to active review assignments?',
        answers: [
          'Active review assignments remain available.',
          'The system checks for conflicts before completing the merge.'
        ]
      },
      {
        question:
          'What happens if the reviewer becomes the author of the same manuscript after merging?',
        answers: [
          'Any conflicting reviewer assignment or invitation is cancelled to prevent self-review.'
        ]
      },
      {
        question: 'What happens to completed reviews?',
        answers: [
          'Review history is retained.',
          'Reviewer credits remain associated with the Primary Account.'
        ]
      }
    ]
  },
  {
    title: 'Co-author Records',
    items: [
      {
        question: 'What happens to co-author information?',
        answers: [
          'Co-author references are updated to the Primary Account.',
          'Verification records remain intact.'
        ]
      }
    ]
  },
  {
    title: 'Affiliations',
    items: [
      {
        question: 'What happens to affiliations?',
        answers: [
          'Affiliations from both accounts are retained.',
          'Duplicate affiliations may be consolidated where applicable.'
        ]
      }
    ]
  },
  {
    title: 'Email Addresses',
    items: [
      {
        question: 'What happens to the Secondary Account email?',
        answers: [
          'The email and any other associated emails become alternate emails for the Primary Account.',
          'They cannot be used to create a new account.',
          'Users signing in with the merged emails are redirected to the Primary Account.'
        ]
      }
    ]
  },
  {
    title: 'User Profile',
    items: [
      {
        question: 'What happens to profile information?',
        answers: [
          'Profile information is combined where possible.',
          'Reviewer preferences and user details are retained on the Primary Account.'
        ]
      }
    ]
  },
  {
    title: 'Historical Records',
    items: [
      {
        question: 'What happens to submission history?',
        answers: [
          'Historical records remain available.',
          'All previous activities are accessible from the Primary Account.'
        ]
      },
      {
        question: 'What happens to submitted files?',
        answers: [
          'Submitted manuscript files are not modified.',
          'Publication records remain unchanged.',
          'Only the ownership links are updated.'
        ]
      }
    ]
  },
  {
    title: 'When is an Account Merge Not Allowed?',
    subtitle: 'An account merge cannot be completed when:',
    lines: [
      'Both accounts have live author submissions.',
      'The account with the live submission is selected as the Secondary Account.'
    ]
  }
] as const;

export type MergeCountLabels = {
  activeSubmissions: string;
  draftSubmissions: string;
  archiveSubmissions: string;
  inProgressReviews: string;
  completedReviews: string;
  invitedReviews: string;
  totalReviews: string;
};

const MERGE_COUNT_LABELS: MergeCountLabels = {
  activeSubmissions: 'Active Submissions',
  draftSubmissions: 'Draft Submissions',
  archiveSubmissions: 'Archive Submissions',
  inProgressReviews: 'In Progress Reviews',
  completedReviews: 'Completed Reviews',
  invitedReviews: 'Invited Reviews',
  totalReviews: 'Total Reviews'
};

type SearchField = Exclude<keyof IMergeAccountSearchFilter, 'duplicateBy'>;

type MergeSearchRow = {
  id: string;
  field: SearchField;
  value: string;
};

type SearchCriteriaForm = {
  rows: MergeSearchRow[];
};

type MergeFieldKey =
  | 'primaryAccount'
  | 'orcid'
  | 'affiliation'
  | 'roles'
  | 'tag'
  | 'submissionCounts'
  | 'reviewCounts';
type SingleSelectMergeFieldKey = Exclude<
  MergeFieldKey,
  'tag' | 'submissionCounts' | 'reviewCounts'
>;
type MultiSelectMergeFieldKey = Extract<MergeFieldKey, 'tag'>;

type SelectedMergeUser = IUsersListItemResponse & {
  displayName: string;
  roleLabels: string[];
  orcidValue: string;
  primaryAccountValue: string;
  tagDetails: MergeUserTagSummary[];
  tagLabels: string[];
  submissionsCount: number;
  totalReviewsCount: number;
  inProgressReviewCount: number;
  completedReviewCount: number;
  invitedReviewCount: number;
};

const MERGE_FIELD_KEYS: MergeFieldKey[] = [
  'primaryAccount',
  'orcid',
  'affiliation',
  'roles',
  'tag',
  'submissionCounts',
  'reviewCounts'
];

const DEFAULT_SEARCH_FIELDS: IMergeAccountSearchField[] = [
  { label: 'First Name', value: 'firstName' },
  { label: 'Last Name', value: 'lastName' },
  { label: 'Email', value: 'email' },
  { label: 'ORCID', value: 'orcid' },
  { label: 'Affiliation', value: 'affiliation' }
];

const defaultValues: SearchCriteriaForm = {
  rows: [{ id: 'row-1', field: 'firstName', value: '' }]
};

const DUPLICATE_ACCOUNT_OPTIONS: {
  label: string;
  value: DuplicateAccountField;
}[] = [
  { label: 'First Name/ Last Name (By Partial)', value: 'fullName' },
  { label: 'Email (By Partial)', value: 'email' },
  { label: 'ORCID', value: 'orcid' },
  { label: 'Affiliation', value: 'affiliation' },
  { label: 'No ORCID', value: 'emptyOrcid' }
];
const DEFAULT_DUPLICATE_ACCOUNT_FIELD: DuplicateAccountField = 'fullName';

interface MergeAccountsPanelProps {
  workspaceId?: string;
  workspaceShortname?: string;
  organizationName?: string;
}

const getUserFullName = (user: IUsersListItemResponse) =>
  `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;

const toCapitalizedLabel = (value: string) =>
  value
    .split(/[\s-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getUserRoleLabels = (user: IUsersListItemResponse) => {
  if (Array.isArray(user.roles) && user.roles.length) {
    return user.roles.map(toCapitalizedLabel);
  }

  const journalRoles = (
    user.journals as Array<{ roles?: string[] } & Record<string, unknown>>
  )?.flatMap(journal => journal.roles ?? []);

  return Array.from(new Set(journalRoles ?? [])).map(toCapitalizedLabel);
};

const getUserTags = (user: IUsersListItemResponse): MergeUserTagSummary[] =>
  Array.from(
    (user.tags ?? []).reduce((tagsMap, tag) => {
      const nextTag = tag as
        | {
            description?: string | null;
            name?: string | null;
            tagCategory?: string | null;
            tagColor?: string | null;
            tagDescription?: string | null;
            usage?: 'users' | 'submissions' | null;
          }
        | null
        | undefined;
      const tagName = nextTag?.name?.trim();

      if (!tagName || tagsMap.has(tagName)) {
        return tagsMap;
      }

      tagsMap.set(tagName, {
        description: nextTag?.description?.trim() || undefined,
        name: tagName,
        color: nextTag?.tagColor?.trim() || undefined,
        tagCategory: nextTag?.tagCategory?.trim() || undefined,
        tagDescription: nextTag?.tagDescription?.trim() || undefined,
        usage: nextTag?.usage ?? 'users'
      });

      return tagsMap;
    }, new Map<string, MergeUserTagSummary>())
  ).map(([, tag]) => tag);

const getUserTagLabels = (user: IUsersListItemResponse) =>
  getUserTags(user).map(tag => tag.name);

const normalizeComparisonValues = (values: string[]) =>
  values
    .map(value => value.trim().toLowerCase())
    .filter(Boolean)
    .sort()
    .join('|');

const normalizeMergeUser = (
  user: IUsersListItemResponse
): SelectedMergeUser => ({
  ...user,
  displayName: getUserFullName(user),
  roleLabels: getUserRoleLabels(user),
  orcidValue: user.orcid || '-',
  primaryAccountValue: getUserFullName(user),
  tagDetails: getUserTags(user),
  tagLabels: getUserTagLabels(user),
  submissionsCount: user.submissionsCount ?? 0,
  totalReviewsCount: user.totalReviewsCount ?? 0,
  inProgressReviewCount: user.inProgressReviewCount ?? 0,
  completedReviewCount: user.completedReviewCount ?? 0,
  invitedReviewCount: user.invitedReviewCount ?? 0
});

const isUserMergeBlocked = (user: { submissionsCount?: number }) =>
  (user.submissionsCount ?? 0) > 0;

const getFieldValue = (
  user: SelectedMergeUser,
  field: MergeFieldKey
): string | string[] => {
  switch (field) {
    case 'primaryAccount':
      return user.primaryAccountValue;
    case 'orcid':
      return user.orcidValue;
    case 'affiliation':
      return (
        user.affiliationDetails?.organizationName || user.affiliation || '-'
      );
    case 'roles':
      return user.roleLabels;
    case 'tag':
      return user.tagLabels;
    case 'submissionCounts':
    case 'reviewCounts':
      return '-';
    default:
      return '-';
  }
};

const getNormalizedFieldValue = (
  user: SelectedMergeUser,
  field: MergeFieldKey
) => {
  if (field === 'primaryAccount') {
    const name = user.primaryAccountValue.trim().toLowerCase();
    const email = (user.email ?? '').trim().toLowerCase();
    return `${name}|${email}`;
  }

  const value = getFieldValue(user, field);

  if (Array.isArray(value)) {
    return normalizeComparisonValues(value);
  }

  return value.trim().toLowerCase();
};

const getFieldDisplayText = (user: SelectedMergeUser, field: MergeFieldKey) => {
  const value = getFieldValue(user, field);

  return Array.isArray(value) ? value.join(', ') || '-' : value;
};

const getSearchFieldPlaceholder = (field: SearchField) => {
  switch (field) {
    case 'firstName':
      return 'Enter first name';
    case 'lastName':
      return 'Enter last name';
    case 'email':
      return 'Enter email';
    case 'orcid':
      return 'Enter ORCID';
    case 'affiliation':
      return 'Enter affiliation';
    default:
      return 'Enter value';
  }
};

const getDuplicateAccountLabel = (field: DuplicateAccountField | null) =>
  DUPLICATE_ACCOUNT_OPTIONS.find(option => option.value === field)?.label ??
  'Selected field';

const getAffiliationAddressLine = (details: IAffiliationDetails) => {
  const { streetAddress, country, countryCode } = details;
  const countryLabel = [country, countryCode && `(${countryCode})`]
    .filter(Boolean)
    .join(' ');

  return [streetAddress, countryLabel].filter(Boolean).join(', ');
};

const MergeAffiliationSearchInput = ({
  name,
  value,
  placeholder,
  onChange
}: {
  name: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedAffiliationName, setSelectedAffiliationName] = useState<
    string | null
  >(null);
  const {
    data: affiliationResults,
    isLoading,
    isError,
    mutateAsync: searchAffiliations
  } = useMutation({
    mutationFn: (searchTerm: string) =>
      fetchRinggoldOrganization(searchTerm, 0, 10)
  });

  useEffect(() => {
    const searchTerm = value.trim();

    if (searchTerm.length < 3) {
      setHasSearched(false);
      onClose();
      return;
    }

    if (selectedAffiliationName === searchTerm) {
      onClose();
      return;
    }

    const timeout = setTimeout(async () => {
      setHasSearched(true);
      onOpen();
      await searchAffiliations(searchTerm);
    }, 500);

    return () => clearTimeout(timeout);
  }, [onClose, onOpen, searchAffiliations, selectedAffiliationName, value]);

  const handleSelectAffiliation = (affiliation: IOrganizations) => {
    setSelectedAffiliationName(affiliation.name);
    onChange(affiliation.name);
    onClose();
  };

  const renderAffiliationResults = () => {
    if (isLoading) {
      return (
        <Flex flexDirection="column" gap={3} alignItems="center" padding={4}>
          <Spinner fontSize={24} />
          <Text textStyle="textBody">Fetching affiliation results...</Text>
        </Flex>
      );
    }

    if (isError || (hasSearched && !affiliationResults?.organizations.length)) {
      return (
        <Flex flexDirection="column" gap={1} padding={4}>
          <Text textStyle="headingItem">No matching affiliations found</Text>
          <Text textStyle="textBody">
            Continue typing or search with the affiliation text you entered.
          </Text>
        </Flex>
      );
    }

    return (
      <List display="flex" flexDirection="column" gap={3} padding={0}>
        {affiliationResults?.organizations.map(affiliation => (
          <ListItem
            key={affiliation.ringgoldId}
            cursor="pointer"
            padding={2}
            borderRadius="sm"
            _hover={{ backgroundColor: 'neutralSecondaryFill' }}
            onClick={() => handleSelectAffiliation(affiliation)}
          >
            <Text textStyle="headingItem">{affiliation.name}</Text>
            {affiliation.topLevelOrganizationName ? (
              <Text textStyle="textContext">
                {affiliation.topLevelOrganizationName}
              </Text>
            ) : null}
            {affiliation.formattedLocation ? (
              <Text textStyle="textContext">
                {affiliation.formattedLocation}
              </Text>
            ) : null}
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom"
      closeOnBlur
      matchWidth
      autoFocus={false}
    >
      <PopoverTrigger>
        <Input
          name={name}
          width="100%"
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => {
            if (
              value.trim().length >= 3 &&
              selectedAffiliationName !== value.trim()
            ) {
              onOpen();
            }
          }}
          onChange={e => {
            setSelectedAffiliationName(null);
            onChange(e.currentTarget.value);
          }}
        />
      </PopoverTrigger>
      <PopoverContent
        minW={{ base: '100%' }}
        paddingY={3}
        paddingX={2}
        maxHeight={isLoading || !affiliationResults ? 'none' : '264px'}
        overflowY={isLoading || !affiliationResults ? 'unset' : 'auto'}
      >
        <PopoverBody padding={0}>{renderAffiliationResults()}</PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const fetchDuplicateAccountsPage = async ({
  workspaceId,
  filters,
  offset
}: {
  workspaceId: string;
  filters?: IMergeAccountSearchFilter;
  offset: number;
}) => {
  return fetchWorkspaceMergeAccountSearch({
    workspaceId,
    limit: DUPLICATE_ACCOUNT_FETCH_LIMIT,
    offset,
    filters
  });
};

const MergeAccountsPanel = ({
  workspaceId: providedWorkspaceId,
  workspaceShortname,
  organizationName
}: MergeAccountsPanelProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { shortname: routeShortname = '' } = useParams();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedUsersCache, setSelectedUsersCache] = useState<
    Record<string, IUsersListItemResponse>
  >({});
  const [selectedProfileUser, setSelectedProfileUser] =
    useState<IUsersListItemResponse | null>(null);
  const [appliedRows, setAppliedRows] = useState<MergeSearchRow[]>(
    defaultValues.rows
  );
  const [mergeCandidates, setMergeCandidates] = useState<
    SelectedMergeUser[] | null
  >(null);
  const [fieldSelections, setFieldSelections] = useState<
    Partial<Record<SingleSelectMergeFieldKey, string>>
  >({});
  const [multiFieldSelections, setMultiFieldSelections] = useState<
    Partial<Record<MultiSelectMergeFieldKey, string[]>>
  >({});
  const [isConfirmMergeModalOpen, setIsConfirmMergeModalOpen] = useState(false);
  const [
    isFetchDuplicateAccountsModalOpen,
    setIsFetchDuplicateAccountsModalOpen
  ] = useState(false);
  const [selectedDuplicateAccountField, setSelectedDuplicateAccountField] =
    useState<DuplicateAccountField>(DEFAULT_DUPLICATE_ACCOUNT_FIELD);
  const [activeDuplicateAccountField, setActiveDuplicateAccountField] =
    useState<DuplicateAccountField | null>(null);
  const resolveAutoFillDoneRef = useRef(false);
  const [isMergedProfileOpen, setIsMergedProfileOpen] = useState(false);
  const [isMergeTooltipOpen, setIsMergeTooltipOpen] = useState(false);
  const {
    isOpen: isMergeHelpModalOpen,
    onOpen: openMergeHelpModal,
    onClose: closeMergeHelpModal
  } = useDisclosure();
  const mergeHelpModalWidth = useBreakpointValue({
    base: 'calc(100vw - 32px)',
    md: '920px'
  });
  const [mergeSuccessData, setMergeSuccessData] = useState<{
    mergedCount: number;
    mergedProfile: {
      userId: string;
      fullName: string;
      email: string;
      submissions: string;
      draftSubmissions: string;
      archiveSubmissions: string;
      inProgressReviews: string;
      completedReviews: string;
      invitedReviews: string;
      totalReviews: string;
      orcid: string;
      role: string;
      roles: string[];
      journals: IJournalWiseUserRoleList[];
      tags: MergeUserTagSummary[];
      affiliation: string;
      affiliationDetails?: IAffiliationDetails;
    };
  } | null>(null);
  const {
    workspaceId: fallbackWorkspaceId,
    workspaceShortname: fallbackShortname
  } = useWorkspace();
  const workspaceId = providedWorkspaceId ?? fallbackWorkspaceId;
  const shortname = workspaceShortname ?? fallbackShortname ?? routeShortname;
  const shouldFetchOrganizationName = !organizationName && Boolean(shortname);

  const mergeAccountsMutation = useWorkspaceMergeAccounts(workspaceId ?? '');

  const { watch, setValue, reset } = useForm<SearchCriteriaForm>({
    defaultValues
  });
  const rows = watch('rows');
  const canAddSearchRow = rows.length < MAX_SEARCH_ROWS;

  const searchFilters = useMemo<IMergeAccountSearchFilter>(
    () =>
      appliedRows.reduce<IMergeAccountSearchFilter>((acc, row) => {
        const trimmedValue = row.value.trim();
        if (!trimmedValue) {
          return acc;
        }

        const currentValues = acc[row.field as SearchField] ?? [];
        return {
          ...acc,
          [row.field]: [
            ...(Array.isArray(currentValues) ? currentValues : []),
            trimmedValue
          ]
        };
      }, {}),
    [appliedRows]
  );

  const { data: organization } = useQuery({
    queryKey: [`organization-${shortname}`],
    queryFn: () => (shortname ? fetchOrganization(shortname) : null),
    enabled: shouldFetchOrganizationName,
    retry: 0
  });

  const { data: searchFieldResponse } = useQuery({
    queryKey: ['merge-accounts-search-fields', workspaceId],
    queryFn: () => fetchWorkspaceMergeAccountSearchFields(workspaceId!),
    enabled: Boolean(workspaceId)
  });

  const searchFieldOptions = searchFieldResponse?.items.length
    ? searchFieldResponse.items
    : DEFAULT_SEARCH_FIELDS;

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError
  } = useInfiniteQuery({
    queryKey: ['merge-accounts-users-list', { workspaceId, searchFilters }],
    queryFn: ({ pageParam = 0 }) =>
      fetchWorkspaceMergeAccountSearch({
        workspaceId: workspaceId!,
        limit: LIMIT,
        offset: pageParam,
        filters: searchFilters
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined;
      const loaded = allPages.reduce(
        (sum, page) => sum + (page?.items?.length ?? 0),
        0
      );
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: Boolean(workspaceId) && !activeDuplicateAccountField
  });

  const {
    data: duplicateData,
    isLoading: isDuplicateLoading,
    isFetching: isDuplicateFetching,
    isFetchingNextPage: isDuplicateFetchingNextPage,
    hasNextPage: hasDuplicateNextPage,
    fetchNextPage: fetchDuplicateNextPage
  } = useInfiniteQuery({
    queryKey: [
      'merge-accounts-duplicate-accounts',
      { workspaceId, searchFilters, activeDuplicateAccountField }
    ],
    queryFn: ({ pageParam = 0 }) => {
      const duplicateFilters: IMergeAccountSearchFilter = {
        ...searchFilters,
        duplicateBy: activeDuplicateAccountField!
      };
      return fetchDuplicateAccountsPage({
        workspaceId: workspaceId!,
        offset: pageParam,
        filters: duplicateFilters
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined;
      const loaded = allPages.reduce(
        (sum, page) => sum + (page?.items?.length ?? 0),
        0
      );
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: Boolean(workspaceId) && Boolean(activeDuplicateAccountField)
  });

  const hasDuplicateAccountResults = Boolean(activeDuplicateAccountField);
  const visibleItems = useMemo(
    () =>
      (hasDuplicateAccountResults ? duplicateData : data)?.pages.flatMap(
        page => page?.items ?? []
      ) ?? [],
    [data, duplicateData, hasDuplicateAccountResults]
  );

  const displayOrganizationName =
    organizationName || organization?.name || shortname;
  const totalUsers = hasDuplicateAccountResults
    ? duplicateData?.pages[0]?.total ?? 0
    : data?.pages[0]?.total ?? 0;
  const isLoadingMore = hasDuplicateAccountResults
    ? isDuplicateFetchingNextPage
    : isFetchingNextPage;
  const canLoadMore = hasDuplicateAccountResults
    ? Boolean(hasDuplicateNextPage)
    : Boolean(hasNextPage);
  const loadMore = useCallback(() => {
    if (hasDuplicateAccountResults) {
      if (hasDuplicateNextPage && !isDuplicateFetchingNextPage) {
        void fetchDuplicateNextPage();
      }
    } else if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [
    fetchDuplicateNextPage,
    fetchNextPage,
    hasDuplicateAccountResults,
    hasDuplicateNextPage,
    hasNextPage,
    isDuplicateFetchingNextPage,
    isFetchingNextPage
  ]);
  const hasSearchValues = appliedRows.some(row => row.value.trim());
  const canMerge =
    selectedUsers.length >= MIN_MERGE_ACCOUNTS &&
    selectedUsers.length <= MAX_MERGE_ACCOUNTS;
  const isSelectionLimitReached = selectedUsers.length >= MAX_MERGE_ACCOUNTS;
  const inResolveMode = !!mergeCandidates?.length;
  const availableMergeFields = MERGE_FIELD_KEYS;

  const filtersKey = useMemo(
    () =>
      JSON.stringify(
        appliedRows.map(row => ({
          field: row.field,
          value: row.value.trim()
        }))
      ),
    [appliedRows]
  );

  useEffect(() => {
    setMergeCandidates(null);
    setMergeSuccessData(null);
    setFieldSelections({});
    setMultiFieldSelections({});
    setIsMergedProfileOpen(false);
    setIsFetchDuplicateAccountsModalOpen(false);
  }, [filtersKey, activeDuplicateAccountField]);

  useEffect(() => {
    if (!mergeCandidates?.length) {
      resolveAutoFillDoneRef.current = false;
      setFieldSelections({});
      setMultiFieldSelections({});
      return;
    }

    if (resolveAutoFillDoneRef.current) {
      return;
    }
    resolveAutoFillDoneRef.current = true;

    const identicalFields = availableMergeFields.filter(
      field =>
        new Set(
          mergeCandidates.map(user => getNormalizedFieldValue(user, field))
        ).size <= 1
    );

    setFieldSelections({
      ...identicalFields.reduce(
        (acc, field) => ({
          ...acc,
          ...(field !== 'tag' ? { [field]: mergeCandidates[0]?.userId } : {})
        }),
        {} as Partial<Record<SingleSelectMergeFieldKey, string>>
      )
    });
    setMultiFieldSelections({
      ...(identicalFields.includes('tag')
        ? { tag: mergeCandidates.map(user => user.userId) }
        : {})
    });
  }, [mergeCandidates]);

  const addSearchRow = () => {
    if (rows.length >= MAX_SEARCH_ROWS) {
      return;
    }

    setValue('rows', [
      ...rows,
      {
        id: `row-${Date.now()}`,
        field: 'firstName',
        value: ''
      }
    ]);
  };

  const removeSearchRow = (id: string) => {
    if (rows.length === 1) {
      reset(defaultValues);
      return;
    }

    setValue(
      'rows',
      rows.filter(row => row.id !== id)
    );
  };

  const updateSearchRow = (
    id: string,
    key: keyof MergeSearchRow,
    value: string
  ) => {
    setValue(
      'rows',
      rows.map(row => (row.id === id ? { ...row, [key]: value } : row))
    );
  };

  const handleReset = () => {
    reset(defaultValues);
    setAppliedRows(defaultValues.rows);
    setSelectedDuplicateAccountField(DEFAULT_DUPLICATE_ACCOUNT_FIELD);
    setActiveDuplicateAccountField(null);
  };

  const handleSearch = () => {
    if (!rows.some(row => row.value.trim())) {
      toast({
        variant: 'attention',
        title: 'Please enter a value.',
        isClosable: true
      });
      return;
    }

    setAppliedRows(rows);
  };

  const toggleUserSelection = (user: IUsersListItemResponse) => {
    const { userId } = user;
    const isSelected = selectedUsers.includes(userId);

    if (isSelected) {
      setSelectedUsers(current => current.filter(id => id !== userId));
      setSelectedUsersCache(current => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
      return;
    }

    setSelectedUsers(current => [...current, userId]);
    setSelectedUsersCache(current => ({ ...current, [userId]: user }));
  };

  const handleMerge = () => {
    if (selectedUsers.length < MIN_MERGE_ACCOUNTS) {
      toast({
        variant: 'attention',
        title: `Select at least ${MIN_MERGE_ACCOUNTS} accounts`,
        description: `Choose between ${MIN_MERGE_ACCOUNTS} and ${MAX_MERGE_ACCOUNTS} user accounts before continuing.`,
        isClosable: true
      });
      return;
    }

    if (selectedUsers.length > MAX_MERGE_ACCOUNTS) {
      toast({
        variant: 'attention',
        title: `Select up to ${MAX_MERGE_ACCOUNTS} accounts`,
        description: `Choose no more than ${MAX_MERGE_ACCOUNTS} user accounts before continuing.`,
        isClosable: true
      });
      return;
    }

    if (!canMerge) return;
    const nextCandidates = selectedUsers
      .map(userId => selectedUsersCache[userId])
      .filter((u): u is IUsersListItemResponse => Boolean(u))
      .map(normalizeMergeUser);

    resolveAutoFillDoneRef.current = false;
    setMergeCandidates(nextCandidates);
  };

  const handleFetchDuplicateAccounts = () => {
    if (!workspaceId) {
      return;
    }

    setActiveDuplicateAccountField(selectedDuplicateAccountField);
    setIsFetchDuplicateAccountsModalOpen(false);
  };

  const handleBackToMergeSearch = () => {
    setMergeCandidates(null);
    setIsConfirmMergeModalOpen(false);
  };

  const handleBackToUserList = () => {
    reset(defaultValues);
    setAppliedRows(defaultValues.rows);
    setSelectedDuplicateAccountField(DEFAULT_DUPLICATE_ACCOUNT_FIELD);
    setActiveDuplicateAccountField(null);
    setMergeSuccessData(null);
    setMergeCandidates(null);
    setSelectedUsers([]);
    setSelectedUsersCache({});
    setIsMergedProfileOpen(false);
    setIsConfirmMergeModalOpen(false);
    void queryClient.invalidateQueries({
      queryKey: ['merge-accounts-users-list']
    });
  };

  const handleRemoveMergeCandidate = (userId: string) => {
    setMergeCandidates(current => {
      if (!current || current.length <= MIN_MERGE_ACCOUNTS) return current;
      return current.filter(user => user.userId !== userId);
    });
    setSelectedUsers(current => current.filter(id => id !== userId));
    setSelectedUsersCache(current => {
      const next = { ...current };
      delete next[userId];
      return next;
    });
  };

  const conflictFields: MergeFieldKey[] = useMemo(() => {
    if (!mergeCandidates?.length) return [];

    return availableMergeFields.filter(field => {
      if (
        field === 'roles' ||
        field === 'submissionCounts' ||
        field === 'reviewCounts'
      ) {
        return false;
      }

      return (
        new Set(
          mergeCandidates.map(user => getNormalizedFieldValue(user, field))
        ).size > 1
      );
    });
  }, [mergeCandidates]);

  const autoMergedFields: MergeFieldKey[] = useMemo(() => {
    if (!mergeCandidates?.length) return [];

    return availableMergeFields.filter(field => {
      if (
        field === 'roles' ||
        field === 'submissionCounts' ||
        field === 'reviewCounts'
      ) {
        return true;
      }

      return (
        new Set(
          mergeCandidates.map(user => getNormalizedFieldValue(user, field))
        ).size <= 1
      );
    });
  }, [mergeCandidates]);

  if (isMergedProfileOpen && mergeSuccessData) {
    return (
      <SubmittedAuthorDetailsModal
        backButtonText="Back"
        userId={mergeSuccessData.mergedProfile.userId}
        onBackClick={() => setIsMergedProfileOpen(false)}
        roles={mergeSuccessData.mergedProfile.roles}
        isProxyUserNotRequired={true}
      />
    );
  }

  if (selectedProfileUser) {
    return (
      <SubmittedAuthorDetailsModal
        backButtonText="Back"
        userId={selectedProfileUser.userId}
        onBackClick={() => setSelectedProfileUser(null)}
        roles={selectedProfileUser.roles}
        isProxyUserNotRequired={true}
      />
    );
  }

  if (mergeSuccessData) {
    return (
      <Overlay>
        <MergeAccountsSuccessState
          mergedCount={mergeSuccessData.mergedCount}
          mergedProfile={mergeSuccessData.mergedProfile}
          onBackToUserList={handleBackToUserList}
          onViewMergedProfile={() => setIsMergedProfileOpen(true)}
          countLabels={MERGE_COUNT_LABELS}
        />
      </Overlay>
    );
  }

  const renderFieldLabel = (field: MergeFieldKey) => {
    switch (field) {
      case 'primaryAccount':
        return 'Primary Account';
      case 'orcid':
        return 'ORCiD';
      case 'affiliation':
        return 'Affiliation';
      case 'roles':
        return 'Journal & Roles';
      case 'tag':
        return 'Tags';
      case 'submissionCounts':
        return 'Submissions';
      case 'reviewCounts':
        return 'Reviews';
      default:
        return field;
    }
  };

  const renderFieldDescription = (field: MergeFieldKey) => {
    switch (field) {
      case 'roles':
        return 'All roles will be consolidated into the primary account.';
      case 'tag':
        return 'Select multiple tag sets from the accounts you are merging.';
      case 'submissionCounts':
        return 'All submission counts will be consolidated into the primary account.';
      case 'reviewCounts':
        return 'All review counts will be consolidated into the primary account.';
      default:
        return null;
    }
  };

  if (inResolveMode && mergeCandidates) {
    const fallbackCandidate = mergeCandidates[0]!;
    const blockedMergeCandidates = mergeCandidates.filter(isUserMergeBlocked);
    const selectedPrimaryAccount =
      mergeCandidates.find(
        user => user.userId === fieldSelections.primaryAccount
      ) ?? null;
    const isSelectedPrimaryBlocked = Boolean(
      selectedPrimaryAccount && isUserMergeBlocked(selectedPrimaryAccount)
    );
    const isSelectedPrimaryBlockedColumn = (userId: string) =>
      isSelectedPrimaryBlocked && selectedPrimaryAccount?.userId === userId;
    const canReview =
      Boolean(selectedPrimaryAccount) &&
      (blockedMergeCandidates.length === 0 ||
        (blockedMergeCandidates.length === 1 && isSelectedPrimaryBlocked));
    const conflictFieldsWithoutPrimary = conflictFields.filter(
      field => field !== 'primaryAccount'
    );
    const handlePrimaryAccountSelection = (userId: string) => {
      setFieldSelections(current => ({
        ...current,
        primaryAccount: userId,
        orcid: userId,
        affiliation: userId
      }));
      setMultiFieldSelections(current => ({
        ...current,
        tag: [userId]
      }));
    };
    const getSelectedUserForField = (field: SingleSelectMergeFieldKey) =>
      mergeCandidates.find(user => fieldSelections[field] === user.userId) ??
      fallbackCandidate;
    const primaryAccount = selectedPrimaryAccount ?? fallbackCandidate;
    const mergedRoles = Array.from(
      new Set(mergeCandidates.flatMap(user => user.roleLabels))
    );
    const selectedTagUsers = mergeCandidates.filter(user =>
      (multiFieldSelections.tag ?? []).includes(user.userId)
    );
    const mergedTagDetails = Array.from(
      new Map(
        selectedTagUsers
          .flatMap(user => user.tagDetails)
          .map(tag => [tag.name, tag] as const)
      ).values()
    );
    const totalSubmissions = mergeCandidates.reduce(
      (sum, user) => sum + user.submissionsCount,
      0
    );
    const totalDraftSubmissions = mergeCandidates.reduce(
      (sum, user) => sum + (user.draftSubmissionsCount ?? 0),
      0
    );
    const totalArchiveSubmissions = mergeCandidates.reduce(
      (sum, user) => sum + (user.archiveSubmissionsCount ?? 0),
      0
    );
    const totalReviews = mergeCandidates.reduce(
      (sum, user) => sum + user.totalReviewsCount,
      0
    );
    const totalInProgressReviews = mergeCandidates.reduce(
      (sum, user) => sum + user.inProgressReviewCount,
      0
    );
    const totalCompletedReviews = mergeCandidates.reduce(
      (sum, user) => sum + user.completedReviewCount,
      0
    );
    const totalInvitedReviews = mergeCandidates.reduce(
      (sum, user) => sum + user.invitedReviewCount,
      0
    );
    const affiliationSourceUser = getSelectedUserForField('affiliation');
    const mergedProfile = {
      userId: primaryAccount.userId,
      fullName: getFieldDisplayText(
        getSelectedUserForField('primaryAccount'),
        'primaryAccount'
      ),
      orcid: getFieldDisplayText(getSelectedUserForField('orcid'), 'orcid'),
      email: primaryAccount.email || '-',
      role: mergedRoles.join(', ') || '-',
      roles: mergedRoles,
      journals: Object.values(
        mergeCandidates
          .flatMap(u => (u.journals ?? []) as IJournalWiseUserRoleList[])
          .reduce<Record<string, IJournalWiseUserRoleList>>((acc, journal) => {
            const existing = acc[journal.id];
            acc[journal.id] = {
              id: journal.id,
              name: journal.name,
              shortname: journal.shortname,
              roles: existing
                ? Array.from(new Set([...existing.roles, ...journal.roles]))
                : [...journal.roles]
            };
            return acc;
          }, {})
      ),
      tags: mergedTagDetails,
      affiliation: getFieldDisplayText(affiliationSourceUser, 'affiliation'),
      affiliationDetails: affiliationSourceUser.affiliationDetails,
      submissions: `${totalSubmissions}`,
      draftSubmissions: `${totalDraftSubmissions}`,
      archiveSubmissions: `${totalArchiveSubmissions}`,
      inProgressReviews: `${totalInProgressReviews}`,
      completedReviews: `${totalCompletedReviews}`,
      invitedReviews: `${totalInvitedReviews}`,
      totalReviews: `${totalReviews}`
    };
    const primaryAccountSummary = {
      userId: primaryAccount.userId,
      name: primaryAccount.displayName,
      email: primaryAccount.email || '-'
    };
    const accountsToDeactivate = mergeCandidates
      .filter(user => user.userId !== primaryAccount.userId)
      .map(user => ({
        userId: user.userId,
        name: user.displayName,
        email: user.email || '-'
      }));
    const handleConfirmMerge = async () => {
      const candidateUserIds = new Set(mergeCandidates.map(u => u.userId));
      const validTagSourceUserIds = (multiFieldSelections.tag ?? []).filter(
        id => candidateUserIds.has(id)
      );
      const result = await mergeAccountsMutation.mutateAsync({
        primaryUserId: primaryAccount.userId,
        secondaryUserIds: accountsToDeactivate.map(u => u.userId),
        orcidSourceUserId: fieldSelections.orcid ?? primaryAccount.userId,
        affiliationSourceUserId:
          fieldSelections.affiliation ?? primaryAccount.userId,
        tagSourceUserIds: validTagSourceUserIds
      });

      if (result.isSuccess) {
        setIsConfirmMergeModalOpen(false);
        setMergeSuccessData({
          mergedCount: mergeCandidates.length,
          mergedProfile: {
            ...mergedProfile,
            submissions: `${result.submissionsCount}`,
            draftSubmissions: `${result.draftSubmissionsCount}`,
            archiveSubmissions: `${result.archiveSubmissionsCount}`,
            inProgressReviews: `${result.inProgressReviewCount}`,
            completedReviews: `${result.completedReviewCount}`,
            invitedReviews: `${result.invitedReviewCount}`,
            totalReviews: `${result.totalReviewsCount}`
          }
        });
      } else {
        toast({
          variant: 'error',
          title: 'Unable to merge accounts',
          description:
            'We encountered an issue while merging accounts. Please try again.',
          isClosable: true
        });
      }
    };
    const toggleMultiSelection = (
      field: MultiSelectMergeFieldKey,
      userId: string
    ) => {
      setMultiFieldSelections(current => {
        const selectedIds = current[field] ?? [];
        return {
          ...current,
          [field]: selectedIds.includes(userId)
            ? selectedIds.filter(id => id !== userId)
            : [...selectedIds, userId]
        };
      });
    };
    const renderValueContent = (
      user: SelectedMergeUser,
      field: MergeFieldKey
    ) => {
      const value = getFieldValue(user, field);

      if (field === 'primaryAccount') {
        const isDisabled =
          isUserMergeBlocked(user) &&
          !isSelectedPrimaryBlockedColumn(user.userId);
        return (
          <Flex direction="column" gap={1} minWidth={0} width="100%">
            <Flex alignItems="center" gap={1.5} flexWrap="wrap">
              <Text
                textStyle="headingItem"
                whiteSpace="normal"
                wordBreak="break-word"
                overflowWrap="anywhere"
              >
                {user.displayName}
              </Text>
              {isDisabled && (
                <Tooltip
                  label="This account has active submissions or ongoing reviews and cannot be merged at this time."
                  hasArrow
                  placement="top"
                  borderRadius={4}
                >
                  <Box color="#E53E3E" cursor="default" flexShrink={0}>
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                  </Box>
                </Tooltip>
              )}
            </Flex>
            <Text
              textStyle="textBody"
              whiteSpace="normal"
              wordBreak="break-word"
              overflowWrap="anywhere"
            >
              {user.email || '-'}
            </Text>
          </Flex>
        );
      }

      if (field === 'affiliation') {
        if (!user.affiliationDetails) {
          return (
            <Text
              textStyle="textBody"
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              overflowWrap="anywhere"
              paddingTop="1px"
            >
              {user.affiliation || '-'}
            </Text>
          );
        }

        return (
          <Flex direction="column" gap={1}>
            <Flex alignItems="center" gap={2}>
              <Text
                textStyle="headingItem"
                whiteSpace="normal"
                wordBreak="break-word"
                overflowWrap="anywhere"
              >
                {user.affiliationDetails.organizationName}
              </Text>
            </Flex>
            {(user.affiliationDetails.country ||
              user.affiliationDetails.countryCode) && (
              <Text textStyle="textBody">
                {user.affiliationDetails.country}
                {user.affiliationDetails.countryCode &&
                  ` (${user.affiliationDetails.countryCode})`}
              </Text>
            )}
            {user.affiliationDetails.streetAddress && (
              <Text textStyle="textBody">
                {user.affiliationDetails.streetAddress}
              </Text>
            )}
            {(user.affiliationDetails.city ||
              user.affiliationDetails.postalCode) && (
              <Text textStyle="textBody">
                {[
                  user.affiliationDetails.city,
                  user.affiliationDetails.postalCode
                ]
                  .filter(Boolean)
                  .join('- ')}
              </Text>
            )}
          </Flex>
        );
      }

      if (field === 'submissionCounts') {
        const items = [
          {
            label: MERGE_COUNT_LABELS.activeSubmissions,
            value: user.submissionsCount
          },
          {
            label: MERGE_COUNT_LABELS.draftSubmissions,
            value: user.draftSubmissionsCount ?? 0
          },
          {
            label: MERGE_COUNT_LABELS.archiveSubmissions,
            value: user.archiveSubmissionsCount ?? 0
          }
        ];
        return (
          <Flex
            gap={2}
            wrap="wrap"
            alignItems="flex-start"
            minWidth={0}
            width="100%"
          >
            {items.map(({ label, value: count }) => (
              <Badge
                key={label}
                size="context"
                variant="neutral"
                maxWidth="100%"
              >
                <Text
                  as="span"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  overflowWrap="anywhere"
                >
                  {label}: {count}
                </Text>
              </Badge>
            ))}
          </Flex>
        );
      }

      if (field === 'reviewCounts') {
        const reviewItems = [
          {
            label: MERGE_COUNT_LABELS.totalReviews,
            value: user.totalReviewsCount
          },
          {
            label: MERGE_COUNT_LABELS.inProgressReviews,
            value: user.inProgressReviewCount
          },
          {
            label: MERGE_COUNT_LABELS.completedReviews,
            value: user.completedReviewCount
          },
          {
            label: MERGE_COUNT_LABELS.invitedReviews,
            value: user.invitedReviewCount
          }
        ];
        return (
          <Flex
            gap={2}
            wrap="wrap"
            alignItems="flex-start"
            minWidth={0}
            width="100%"
          >
            {reviewItems.map(({ label, value }) => (
              <Badge
                key={label}
                size="context"
                variant="neutral"
                maxWidth="100%"
              >
                <Text
                  as="span"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  overflowWrap="anywhere"
                >
                  {label}: {value}
                </Text>
              </Badge>
            ))}
          </Flex>
        );
      }

      if (field === 'roles') {
        return <UserItem data={user.journals as IJournalWiseUserRoleList[]} />;
      }

      if (Array.isArray(value)) {
        if (field === 'tag') {
          return (
            <UserTagBadgeList
              tags={user.tagDetails}
              keyPrefix={`merge-conflict-${field}-${user.userId}`}
              maxVisible={2}
            />
          );
        }

        if (!value.length) {
          return <Text textStyle="textBody">-</Text>;
        }

        return (
          <Flex
            gap={2}
            wrap="wrap"
            alignItems="flex-start"
            minWidth={0}
            width="100%"
          >
            {value.map(item => (
              <Badge
                key={`${field}-${user.userId}-${item}`}
                size="context"
                variant="neutral"
                maxWidth="100%"
              >
                <Text
                  as="span"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  overflowWrap="anywhere"
                >
                  {item}
                </Text>
              </Badge>
            ))}
          </Flex>
        );
      }

      return (
        <Text
          textStyle="textBody"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          overflowWrap="anywhere"
          paddingTop="1px"
        >
          {value}
        </Text>
      );
    };
    const renderFieldRows = (
      fields: MergeFieldKey[],
      options?: { isReadOnly?: boolean }
    ) => (
      <Table
        size="sm"
        minWidth="100%"
        sx={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}
      >
        <Tbody>
          {fields.map(field => (
            <Tr
              key={field}
              background="white"
              borderBottom="1px solid"
              borderColor="#EAECF0"
            >
              <Td p={4} width="260px" verticalAlign="middle">
                <Flex
                  direction="column"
                  justifyContent="center"
                  gap={1}
                  minHeight="100%"
                >
                  <Text textStyle="headingItem">{renderFieldLabel(field)}</Text>
                  {renderFieldDescription(field) ? (
                    <Text textStyle="textContext" color="#344054">
                      {renderFieldDescription(field)}
                    </Text>
                  ) : null}
                </Flex>
              </Td>
              {mergeCandidates.map(user => {
                const isSelectedPrimaryAccount =
                  field === 'primaryAccount' &&
                  fieldSelections.primaryAccount === user.userId;
                const shouldHighlightValue = isSelectedPrimaryAccount;
                const isBlockedColumn =
                  isUserMergeBlocked(user) &&
                  !isSelectedPrimaryBlockedColumn(user.userId);
                const isColumnDisabled =
                  isBlockedColumn && field !== 'primaryAccount';

                return (
                  <Td
                    key={`${field}-${user.userId}`}
                    p={4}
                    verticalAlign="middle"
                    width={`${Math.floor(100 / mergeCandidates.length)}%`}
                    sx={
                      isBlockedColumn
                        ? { backgroundColor: '#FFEAEA !important' }
                        : undefined
                    }
                  >
                    <Flex alignItems="center" width="100%" gap={3}>
                      {field !== 'tag' &&
                      field !== 'roles' &&
                      field !== 'submissionCounts' &&
                      field !== 'reviewCounts' ? (
                        <Box
                          width="28px"
                          minWidth="28px"
                          flexShrink={0}
                          display="flex"
                          alignItems="center"
                          alignSelf="center"
                        >
                          <Radio
                            id={`${field}-${user.userId}`}
                            name={
                              options?.isReadOnly
                                ? `merge-field-${field}-${user.userId}`
                                : `merge-field-${field}`
                            }
                            value={user.userId}
                            ariaLabel={`${field}-${user.userId}`}
                            checked={
                              options?.isReadOnly ||
                              fieldSelections[
                                field as SingleSelectMergeFieldKey
                              ] === user.userId
                            }
                            onChange={
                              options?.isReadOnly || isColumnDisabled
                                ? () => {}
                                : () =>
                                    field === 'primaryAccount'
                                      ? handlePrimaryAccountSelection(
                                          user.userId
                                        )
                                      : setFieldSelections(current => ({
                                          ...current,
                                          [field]: user.userId
                                        }))
                            }
                          >
                            {' '}
                          </Radio>
                        </Box>
                      ) : null}
                      {!options?.isReadOnly && field === 'tag' ? (
                        <Box
                          width="28px"
                          minWidth="28px"
                          flexShrink={0}
                          display="flex"
                          alignItems="center"
                          alignSelf="center"
                        >
                          <Checkbox
                            ariaLabel={`${field}-${user.userId}`}
                            id={`${field}-${user.userId}`}
                            name={`${field}-${user.userId}`}
                            isChecked={(
                              multiFieldSelections.tag ?? []
                            ).includes(user.userId)}
                            disabled={isColumnDisabled}
                            onChange={() =>
                              !isColumnDisabled &&
                              toggleMultiSelection('tag', user.userId)
                            }
                          >
                            {' '}
                          </Checkbox>
                        </Box>
                      ) : null}
                      <Box
                        flex="1"
                        minWidth={0}
                        overflow="hidden"
                        padding={shouldHighlightValue ? 3 : 0}
                        borderRadius={shouldHighlightValue ? 'md' : undefined}
                        border={shouldHighlightValue ? '1px solid' : undefined}
                        borderColor={
                          shouldHighlightValue ? 'actionPrimaryFill' : undefined
                        }
                        backgroundColor={
                          shouldHighlightValue
                            ? 'neutralSecondaryFill'
                            : undefined
                        }
                      >
                        {renderValueContent(user, field)}
                      </Box>
                      {field === 'primaryAccount' &&
                        mergeCandidates.length > MIN_MERGE_ACCOUNTS && (
                          <ButtonText
                            variant="caution"
                            onClick={() =>
                              handleRemoveMergeCandidate(user.userId)
                            }
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </ButtonText>
                        )}
                    </Flex>
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    );

    return (
      <Overlay>
        <Flex
          flexDirection="column"
          backgroundColor="surfacePrimaryFill"
          minH="100vh"
        >
          <NavbarDashboard />
          <Box paddingTop={4} paddingX={4} paddingBottom={6}>
            <Flex flexDirection="column" gap={4}>
              <ButtonText
                leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                maxWidth="fit-content"
                onClick={handleBackToMergeSearch}
              >
                Back
              </ButtonText>

              <Flex
                width="100%"
                justifyContent="space-between"
                alignItems={{ base: 'flex-start', md: 'center' }}
                gap={3}
                flexWrap="wrap"
              >
                <Flex alignItems="center" gap={2}>
                  <Text textStyle="headingGroup">Account Merge</Text>
                  <Tooltip
                    label={RESOLVE_CONFLICTS_TOOLTIP}
                    hasArrow
                    placement="bottom"
                    borderRadius={4}
                  >
                    <Box color="actionPrimaryFill">
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </Box>
                  </Tooltip>
                  <ButtonText
                    onClick={openMergeHelpModal}
                    maxWidth="fit-content"
                  >
                    What Happens When You Merge Accounts?
                  </ButtonText>
                </Flex>
                <Button
                  variant="secondary"
                  isDisabled={!canReview}
                  disabled={!canReview}
                  onClick={() => setIsConfirmMergeModalOpen(true)}
                  width="fit-content"
                >
                  Review & Confirm
                </Button>
              </Flex>

              <Box
                width="100%"
                border="1px solid"
                borderColor="#EAECF0"
                borderRadius="md"
                overflow="hidden"
                bg="surfacePrimaryFill"
              >
                <Box minWidth={{ base: '760px', md: '100%' }} p={4}>
                  {renderFieldRows(['primaryAccount'])}
                </Box>
                <Accordion
                  chakraAccordionProps={{
                    defaultIndex: [0, 1],
                    sx: {
                      '.chakra-accordion__panel': {
                        padding: 0
                      }
                    }
                  }}
                  accordionItemProps={{
                    marginBottom: 0,
                    borderTop: '1px solid',
                    borderColor: 'neutralPrimaryBorder',
                    _first: {
                      borderTop: '1px solid',
                      borderColor: 'neutralPrimaryBorder'
                    }
                  }}
                  items={[
                    {
                      title: `(${autoMergedFields.length}) Auto Merged Fields`,
                      content: (
                        <Box overflowX="auto">
                          <Box minWidth={{ base: '760px', md: '100%' }}>
                            {renderFieldRows(autoMergedFields, {
                              isReadOnly: true
                            })}
                          </Box>
                        </Box>
                      )
                    },
                    {
                      title: `(${conflictFieldsWithoutPrimary.length}) Conflicts - Fields with different values across accounts, choose which to keep.`,
                      content: (
                        <Box overflowX="auto">
                          <Box minWidth={{ base: '760px', md: '100%' }}>
                            {renderFieldRows(conflictFieldsWithoutPrimary)}
                          </Box>
                        </Box>
                      )
                    }
                  ]}
                />
              </Box>
            </Flex>
          </Box>
          {isConfirmMergeModalOpen && (
            <ConfirmMergeModal
              isOpen
              onClose={() => setIsConfirmMergeModalOpen(false)}
              onConfirm={handleConfirmMerge}
              isConfirming={mergeAccountsMutation.isPending}
              mergedProfile={mergedProfile}
              primaryAccount={primaryAccountSummary}
              accountsToDeactivate={accountsToDeactivate}
              countLabels={MERGE_COUNT_LABELS}
            />
          )}
          {isMergeHelpModalOpen && (
            <Modal
              isOpen
              onClose={closeMergeHelpModal}
              width={mergeHelpModalWidth}
              closeButtonProps={{
                hidden: true
              }}
            >
              <Flex
                direction="column"
                maxHeight="85vh"
                overflowY="auto"
                backgroundColor="surfacePrimaryFill"
                gap={6}
                padding={{ base: 4, md: 8 }}
              >
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  gap={4}
                >
                  <Text textStyle="headingSection">
                    {MERGE_ACCOUNT_HELP_TITLE}
                  </Text>
                  <CloseButton onClick={closeMergeHelpModal} />
                </Flex>

                <Divider />

                <Flex direction="column" gap={6}>
                  {MERGE_ACCOUNT_HELP_SECTIONS.map(section => (
                    <Flex
                      key={section.title}
                      direction="column"
                      gap={3}
                      borderBottom="1px solid"
                      borderColor="neutralPrimaryBorder"
                      pb={3}
                    >
                      <Text textStyle="headingGroup">{section.title}</Text>

                      {'subtitle' in section && (
                        <Text textStyle="textBody">{section.subtitle}</Text>
                      )}

                      {'lines' in section && (
                        <List styleType="disc" spacing={2} ps={5}>
                          {section.lines.map(line => (
                            <ListItem key={line}>
                              <Text textStyle="textBody">{line}</Text>
                            </ListItem>
                          ))}
                        </List>
                      )}

                      {'items' in section && (
                        <Flex direction="column" gap={4}>
                          {section.items.map(item => (
                            <Flex
                              key={item.question}
                              direction="column"
                              gap={2}
                            >
                              <Text textStyle="headingItem">
                                {item.question}
                              </Text>
                              <List styleType="disc" spacing={2} ps={7}>
                                {item.answers.map(answer => (
                                  <Box ps={4} key={answer}>
                                    <ListItem>
                                      <Text textStyle="textBody">{answer}</Text>
                                    </ListItem>
                                  </Box>
                                ))}
                              </List>
                            </Flex>
                          ))}
                        </Flex>
                      )}
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            </Modal>
          )}
        </Flex>
      </Overlay>
    );
  }

  return (
    <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={4}>
      <Flex
        width="100%"
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        gap={3}
        flexWrap="wrap"
      >
        <Flex alignItems="center" gap={2}>
          <Text textStyle="headingGroup">Merge Accounts</Text>
          <Tooltip
            label={MERGE_ACCOUNTS_TOOLTIP}
            hasArrow
            placement="bottom"
            borderRadius={4}
            isOpen={isMergeTooltipOpen}
          >
            <Box
              color="actionPrimaryFill"
              cursor="pointer"
              onMouseEnter={() => setIsMergeTooltipOpen(true)}
              onMouseLeave={() => setIsMergeTooltipOpen(false)}
              onClick={() => setIsMergeTooltipOpen(prev => !prev)}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Box>
          </Tooltip>
        </Flex>
        <Flex gap={3} flexWrap="wrap" width={{ base: '100%', md: 'auto' }}>
          <Button
            variant="secondary"
            isDisabled={selectedUsers.length === 0}
            disabled={selectedUsers.length === 0}
            onClick={() => {
              setSelectedUsers([]);
              setSelectedUsersCache({});
            }}
            flex={{ base: '1', md: 'none' }}
          >
            Deselect All
          </Button>
          <Button
            variant="secondary"
            onClick={() => setIsFetchDuplicateAccountsModalOpen(true)}
            flex={{ base: '1', md: 'none' }}
          >
            Fetch Similar Accounts
          </Button>
          <Button
            variant="primary"
            isDisabled={!canMerge}
            disabled={!canMerge}
            onClick={handleMerge}
            flex={{ base: '1', md: 'none' }}
          >
            Merge Accounts
            {selectedUsers.length > 0 ? ` (${selectedUsers.length})` : ''}
          </Button>
        </Flex>
      </Flex>

      <Box
        width="100%"
        border="1px solid"
        borderColor="#EAECF0"
        borderRadius="md"
        bg="surfacePrimaryFill"
        p={4}
      >
        <Flex flexDirection="column" gap={3}>
          {rows.map(row => (
            <Flex
              key={row.id}
              gap={2}
              alignItems="center"
              direction="row"
              width="100%"
            >
              <Box width={{ base: '130px', md: '180px' }} flexShrink={0}>
                <Select
                  value={row.field}
                  onChange={e =>
                    updateSearchRow(
                      row.id,
                      'field',
                      e.currentTarget.value as SearchField
                    )
                  }
                >
                  {searchFieldOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box flex="1" minWidth={0}>
                {row.field === 'affiliation' ? (
                  <MergeAffiliationSearchInput
                    name={`merge-search-${row.id}`}
                    value={row.value}
                    placeholder={getSearchFieldPlaceholder(row.field)}
                    onChange={value => updateSearchRow(row.id, 'value', value)}
                  />
                ) : (
                  <Input
                    name={`merge-search-${row.id}`}
                    width="100%"
                    value={row.value}
                    placeholder={getSearchFieldPlaceholder(row.field)}
                    onChange={e =>
                      updateSearchRow(row.id, 'value', e.currentTarget.value)
                    }
                  />
                )}
              </Box>
              <Flex gap={2} flexShrink={0}>
                <ButtonIcon
                  ariaLabel="Add search row"
                  variant="secondary"
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  disabled={!canAddSearchRow}
                  onClick={addSearchRow}
                />
                <ButtonText
                  variant="caution"
                  isDisabled={rows.length === 1}
                  disabled={rows.length === 1}
                  onClick={() => removeSearchRow(row.id)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </ButtonText>
              </Flex>
            </Flex>
          ))}

          <Divider orientation="horizontal" borderColor="borderSubtle" />

          <Flex
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            direction="row"
          >
            <Box>
              {activeDuplicateAccountField && (
                <Badge size="context" variant="neutral">
                  Fetch similar accounts by:{' '}
                  {getDuplicateAccountLabel(activeDuplicateAccountField)}
                </Badge>
              )}
            </Box>
            <Flex gap={2} flexShrink={0}>
              <Button
                variant="secondary"
                onClick={handleReset}
                flex={{ base: '1', md: 'none' }}
              >
                Reset
              </Button>
              <Button
                variant="primary"
                onClick={handleSearch}
                flex={{ base: '1', md: 'none' }}
              >
                Search
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Box>

      <Flex
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        flexWrap={{ base: 'wrap', lg: 'nowrap' }}
      >
        {(hasDuplicateAccountResults ? isDuplicateLoading : isLoading) ? (
          <Text textStyle="textBody">Searching for matches...</Text>
        ) : (
          <Text textStyle="textBody">
            {totalUsers > 0 ? (
              <>
                Showing <strong>{visibleItems.length}</strong> of{' '}
                <strong>{totalUsers}</strong> users in {displayOrganizationName}
              </>
            ) : (
              <>
                <strong>0</strong> users in {displayOrganizationName}
              </>
            )}
          </Text>
        )}
      </Flex>

      <Box width="100%">
        {(
          hasDuplicateAccountResults
            ? isDuplicateFetching && !visibleItems.length
            : (isLoading || isFetching) && !visibleItems.length
        ) ? (
          <SubmissionLoader
            title={
              hasDuplicateAccountResults
                ? 'Fetching Similar Accounts'
                : 'Loading Search Results'
            }
            subTitle={
              hasDuplicateAccountResults
                ? 'We are retrieving matching accounts. This should only take a moment.'
                : 'We are retrieving your search results. This should only take a moment.'
            }
            bgColor="neutralSecondaryFill"
          />
        ) : totalUsers ? (
          <>
            <Box overflow="visible" width="100%">
              <Table size="sm" minWidth="100%">
                <Thead>
                  <Tr
                    data-testid="merge-accounts-table-header"
                    sx={{
                      '& th': {
                        textTransform: 'none',
                        color: 'black',
                        background: 'white',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                      }
                    }}
                  >
                    <Th p={4} />
                    <Th p={4} width="60px" maxWidth="60px">
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        S.No.
                      </Text>
                    </Th>
                    <Th p={4} width="320px" maxWidth="320px">
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Name
                      </Text>
                    </Th>
                    <Th p={4} minWidth="180px">
                      <Text textStyle="headingItem">Affiliation</Text>
                    </Th>
                    <Th p={4}>
                      <Text textStyle="headingItem">Journal & Roles</Text>
                    </Th>
                    <Th p={4} width="220px" maxWidth="220px">
                      <Text textStyle="headingItem">Tags</Text>
                    </Th>
                    <Th p={4} whiteSpace="nowrap">
                      <Text textStyle="headingItem">Created Date</Text>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {visibleItems.map((user, index) => {
                    const userTags = getUserTags(user);
                    const isChecked = selectedUsers.includes(user.userId);
                    const isSelectionDisabled =
                      !isChecked && isSelectionLimitReached;

                    return (
                      <Tr
                        key={user.userId}
                        data-testid="merge-accounts-table-row"
                        background="white"
                      >
                        <Td p={4}>
                          <Checkbox
                            ariaLabel={`Select ${getUserFullName(user)}`}
                            id={`merge-account-${user.userId}`}
                            name={`merge-account-${user.userId}`}
                            isChecked={isChecked}
                            isDisabled={isSelectionDisabled}
                            disabled={isSelectionDisabled}
                            onChange={() => toggleUserSelection(user)}
                          >
                            {' '}
                          </Checkbox>
                        </Td>
                        <Td p={4}>
                          <Text textStyle="textBody">{index + 1}</Text>
                        </Td>
                        <Td p={4} minWidth="180px">
                          <Box maxWidth="320px">
                            <Tooltip
                              hasArrow
                              borderRadius={4}
                              placement="top"
                              label="View User Profile"
                            >
                              <Text
                                textStyle="headingItem"
                                width="fit-content"
                                color="actionPrimaryFill"
                                cursor="pointer"
                                textDecoration="none"
                                _hover={{
                                  textDecoration: 'underline',
                                  textDecorationColor: 'actionPrimaryFill'
                                }}
                                onClick={() => setSelectedProfileUser(user)}
                              >
                                {getUserFullName(user)}
                              </Text>
                            </Tooltip>
                            <Text textStyle="textContext" isTruncated>
                              {user.orcid}
                            </Text>
                            <Text textStyle="textContext" isTruncated>
                              {user.email}
                            </Text>
                          </Box>
                        </Td>
                        <Td p={4} minWidth="180px">
                          {user.affiliationDetails ? (
                            <Flex direction="column" gap={1}>
                              <Text textStyle="headingItem">
                                {user.affiliationDetails.organizationName}
                              </Text>
                              {getAffiliationAddressLine(
                                user.affiliationDetails
                              ) && (
                                <Text textStyle="textBody">
                                  {getAffiliationAddressLine(
                                    user.affiliationDetails
                                  )}
                                </Text>
                              )}
                            </Flex>
                          ) : (
                            <Text textStyle="textBody">
                              {user.affiliation || '-'}
                            </Text>
                          )}
                        </Td>
                        <Td p={4} textStyle="textBody">
                          <UserItem
                            data={user.journals as IJournalWiseUserRoleList[]}
                          />
                        </Td>
                        <Td p={4} width="220px" maxWidth="220px">
                          <Box maxWidth="220px" overflow="hidden">
                            <UserTagBadgeList
                              tags={userTags}
                              keyPrefix={`merge-search-${user.userId}`}
                              maxVisible={2}
                            />
                          </Box>
                        </Td>
                        <Td p={4} whiteSpace="nowrap">
                          <Text textStyle="textBody">
                            {user.createdAt
                              ? formatDate(new Date(user.createdAt))
                              : '-'}
                          </Text>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
            <Flex justifyContent="center" marginY={4}>
              {canLoadMore && (
                <Button
                  variant="secondary"
                  onClick={loadMore}
                  isLoading={isLoadingMore}
                >
                  Load More
                </Button>
              )}
            </Flex>
          </>
        ) : isError && !hasDuplicateAccountResults ? (
          <SubmissionLoader
            title="Search Failed"
            subTitle="We encountered an issue connecting to the search service. Please check your connection or try again later."
            bgColor="errorPrimaryFill"
          />
        ) : (
          <SubmissionLoader
            title="No Results Found"
            subTitle={
              hasDuplicateAccountResults
                ? `We could not find similar accounts matched by ${getDuplicateAccountLabel(
                    activeDuplicateAccountField
                  )}.`
                : hasSearchValues
                ? 'We could not find any users matching your search criteria. Please adjust your search and try again.'
                : 'No users are available to merge at the moment.'
            }
            bgColor="neutralSecondaryFill"
          />
        )}
      </Box>

      <FetchDuplicateAccountsModal
        isOpen={isFetchDuplicateAccountsModalOpen}
        isLoading={isDuplicateFetching}
        options={DUPLICATE_ACCOUNT_OPTIONS}
        selectedField={selectedDuplicateAccountField}
        onChange={setSelectedDuplicateAccountField}
        onClose={() => setIsFetchDuplicateAccountsModalOpen(false)}
        onFetch={handleFetchDuplicateAccounts}
      />
    </Flex>
  );
};

export default MergeAccountsPanel;
